import { Injectable } from '@angular/core'
import { RelatedContentDataService } from '../../shared/related-content/related-content-data.service'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { createContentListWithAnalytics } from '../../shared/functions/analytics'
import { ContentItem } from './primary-related-content.component'
import { parseISO } from 'date-fns'
import { Article, RankingContainer, ResultContainer, Video } from '@flocasts/flosports30-types/dist/entity'
import { flow } from 'fp-ts/function'
import { FloListResponse } from '@flocasts/flosports30-types/dist/flo-rest-api/response'

const parseNullDate = (i: null | string) => (i == null ? null : parseISO(i))

type AllRelatedContentTypes = Article | Video | ResultContainer | RankingContainer

/** Obtain and adjust recommended content from the API for an Article. */
@Injectable({
  providedIn: 'root'
})
export class RelatedContentService {
  // prettier-ignore
  constructor(
    private readonly relatedContentApi: RelatedContentDataService
  ) {}

  /**
   * This performs processing on any kind of related content.
   *
   * @remarks
   * If you need to process specific content for just one kind of response,
   * do the work within that method. This pipe should be applicable to all
   * kinds of related content.
   */
  private processAllContent = flow(
    map((resp: FloListResponse<AllRelatedContentTypes, {}>) => resp.data),
    // We filter out content with null dates, but it's very hard to capture in
    // the type system :(
    map(data => data.filter(item => item.publish_start_date !== null)),
    map(data =>
      data.map(item => {
        if (item.type !== 'video') return item
        return {
          ...item,
          drill_through_url:
            item.drill_through_link?.slug_uri != null ? `${item.drill_through_link.slug_uri}/videos` : undefined
        }
      })
    ),
    map(data =>
      data.map(item => ({
        ...item,
        publish_start_date: parseNullDate(item.publish_start_date)
      }))
    ),
    map(data => createContentListWithAnalytics(data))
  )

  /** Obtain a list of recommended content to show in the article right rail. */
  public getArticlesVideosResultsRankings(nodeId: number, selfId?: number): Observable<ReadonlyArray<ContentItem>> {
    return this.relatedContentApi.getArticlesVideosResultsRankings(nodeId, selfId).pipe(this.processAllContent)
  }

  public getArticlesVideos(nodeId: number): Observable<ReadonlyArray<ContentItem>> {
    return this.relatedContentApi.getArticlesVideos(nodeId).pipe(this.processAllContent)
  }

  public getRankings(id: number): Observable<ReadonlyArray<ContentItem>> {
    return this.relatedContentApi.getRankings(id).pipe(
      map(r => r.data),
      map(data => data.filter(r => r.publish_start_date !== null)),
      map(data =>
        data.map(r => ({
          ...r,
          publish_start_date: parseNullDate(r.publish_start_date)
        }))
      ),
      map(data => createContentListWithAnalytics(data))
    )
  }
}
